import { useEffect, useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Input, Button, Alert, Progress, Popover, PopoverHeader, PopoverBody } from "reactstrap"
import Select from 'react-select'
import { RepsTypeAhead } from "./TypeAheadComponents"
import { UpdateTaskMutation } from "../Mutations"
import StatusDropDown from "./Components"
import withApolloClient from '../../withApolloClient'
import "./EditTaskPopOver.css"
import { ForeignKeyInput } from "./ForeignKeyComponents"

const ColumnQuery = gql`query ColumnQuery($columnId: ID!){
    column(id: $columnId){
        id
        title
        columnType
        isRequired
        allowedOptions
        contentType{
            id
            appLabel
            model
        }
    }
}`

function EditTaskPopOver({ isOpen, task, closeModal, target, title, name, value, type, maxLength, columnId, ...props }) {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [input, setInput] = useState({})

    useEffect(() => {
        if (value !== undefined) {
            handleInputChange(value)
        }
    }, [isOpen, task?.id, columnId, name, value])

    const { data: columnData, loading: columnLoading, error: columnError } = useQuery(
        ColumnQuery,
        {
            variables: { columnId: columnId },
            fetchPolicy: 'network-only',
            skip: !columnId,
        }
    )

    const canClearCurrentField = () => {
        if (columnId) {
            const column = columnData?.column
            return !!column && column.columnType !== "MIRROR" && !column.isRequired
        }
        return name === "dueDate" || name === "assignedToIds"
    }

    const handleInputChange = (newValue) => {
        if (columnId) {
            setInput({ ...input, columnValues: [{ columnId: columnId, value: newValue }] })
            return
        } else {
            setInput({ [name]: newValue })
        }
        setError(null)
    }

    const getValue = () => {
        if (columnId) {
            const stored = input?.columnValues?.find(cv => cv.columnId === columnId)
            if (stored !== undefined) return stored.value ?? ""
            return value ?? ""
        }
        return input[name] ?? ""
    }

    const boardUsers = task?.board?.allowedUsers?.edges?.map(({ node }) => node).filter(Boolean) || []
    const boardAllowedUserIds = boardUsers.map((user) => user.id)
    const assignableUserOptions = useMemo(() => {
        const allUsers = [
            ...boardUsers,
            ...(task?.assignedTo?.edges?.map(({ node }) => node).filter(Boolean) || []),
        ]
        const seen = new Set()
        return allUsers.filter((user) => {
            if (!user?.id || seen.has(user.id)) return false
            seen.add(user.id)
            return true
        })
    }, [boardUsers, task?.assignedTo])

    const submitTaskUpdate = (queryInput) => {
        try {
            if (!props.apolloClient || !task.id) {
                throw new Error("GQL Client not found")
            }

            if (Object.keys(queryInput).length === 1) {
                return
            }
            setLoading(true)
            props.apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: queryInput },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error updating task")
                }
                if (result.data && result.data.updateTask && result.data.updateTask.ok) {
                    setError("")
                    closeModal("")
                } else {
                    let errorMessage = result.data && result.data.updateTask && result.data.updateTask.errors && result.data.updateTask.errors[0].messages ? result.data.updateTask.errors[0].messages[0] : "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error updating task " + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error updating task", error)
            setError("Error updating task" + error.toString())
            setLoading(false)
        }
    }

    const editTask = (e) => {
        e.preventDefault()
        submitTaskUpdate({ taskId: task.id, ...input })
    }

    const renderColumnInput = () => {
        if (columnLoading || columnError) {
            return <Progress animated color="info" value={100} />
        }
        if (columnData.column.columnType === "MIRROR") {
            return <Alert color="info" className="mb-0">Mirror columns are read-only and update from their parent FK column.</Alert>
        }
        if (columnData.column.columnType === "FK_OBJECT") {
            return <ForeignKeyInput model={columnData.column.contentType.model} defaultValue={getValue()} onChange={(value) => handleInputChange(value)} />
        } else if (columnData.column.columnType === "ENUM") {
            const options = (columnData.column.allowedOptions || []).map(opt => ({ value: opt, label: opt }))
            return <Select options={options} defaultValue={value ? { value, label: value } : null} onChange={(selected) => handleInputChange(selected ? selected.value : null)} isClearable />
        } else if (columnData?.column?.columnType === "BOOLEAN") {
            const currentValue = getValue() || value
            const isChecked = currentValue === "true" || currentValue === true
            return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <span>Yes</span>
                <div className="toggle-switch">
                    <input type="checkbox" className="toggle-switch-checkbox" name={name} id={"taskEdit"} checked={!isChecked} onChange={() => handleInputChange(String(!isChecked))} />
                    <label className="toggle-switch-label" htmlFor={"taskEdit"}>
                        <span className="toggle-switch-inner" />
                        <span className="toggle-switch-switch" />
                    </label>
                </div>
                <span>No</span>
            </div>
        } else if (columnData?.column?.columnType === "URL") {
            return <Input autoComplete={"off"} type="url" maxLength={maxLength ? maxLength : 500} name={name} id={name} value={getValue()} placeholder="https://..." onChange={(e) => { handleInputChange(e.target.value) }} />
        } else if (columnData?.column?.columnType === "DATETIME") {
            return <Input autoComplete={"off"} type="datetime-local" maxLength={maxLength ? maxLength : 200} name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => { handleInputChange(e.target.value) }} />
        } else if (columnData?.column?.columnType === "TIME") {
            return <Input autoComplete={"off"} type="time" name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => { handleInputChange(e.target.value) }} />
        } else if (columnData?.column?.columnType === "DATE") {
            return <Input autoComplete={"off"} type="date" maxLength={maxLength ? maxLength : 200} name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => { handleInputChange(e.target.value) }} />
        } else if (columnData?.column?.columnType === "NUMBER") {
            return <Input autoComplete={"off"} type="number" maxLength={maxLength ? maxLength : 200} name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => {
                const val = e.target.value
                if (val && val.includes('.')) {
                    setError("Only whole numbers are allowed for this field.")
                } else {
                    setError(null)
                    handleInputChange(val)
                }
            }} />
        } else if (columnData?.column?.columnType === "DECIMAL") {
            return <Input autoComplete={"off"} type="number" maxLength={maxLength ? maxLength : 200} name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => { handleInputChange(e.target.value) }} />
        } else {
            return <Input autoComplete={"off"} type={columnData?.column?.columnType || "text"} maxLength={maxLength ? maxLength : 200} name={name} id={name} value={getValue()} placeholder={title} onChange={(e) => { handleInputChange(e.target.value) }} />
        }
    }

    return (
        <Popover trigger="legacy" placement="bottom-end" isOpen={isOpen} target={target} toggle={() => closeModal("")} className={`popover-custom-width ${name === "status" ? "status-editor-popover" : ""}`}>
            {name !== "status" && <PopoverHeader onClick={() => closeModal("")}>{title} <i style={{ float: "right" }} className="fa fa-times-circle" aria-hidden="true"> </i></PopoverHeader>}
            <PopoverBody className={name === "status" ? "status-editor-body" : ""} style={name === "status" ? {} : { textAlign: "center" }}>
                {error && <Alert color="danger">{error}</Alert>}
                <div className='pb-2'>
                    {columnId ? (
                        renderColumnInput()
                    ) : name === "assignedToIds" ?
                        <RepsTypeAhead
                            onChange={(value) => handleInputChange(value)}
                            defaultValue={getValue()}
                            group={[]}
                            allowedRepIds={boardAllowedUserIds}
                            extraRepOptions={assignableUserOptions}
                        />
                        : name === "status" ?
                            <StatusDropDown taskStatus={task.status} taskId={task.id} isAutomated={task.isAutomated} client={props.apolloClient} closeModal={closeModal} />
                            : type === "boolean" ? <div>
                                <span>Yes</span>
                                <div className="toggle-switch">
                                    <input type="checkbox" className="toggle-switch-checkbox" name={name} id={"taskEdit"} checked={!input[name]} onChange={() => handleInputChange(!input[name])} />
                                    <label className="toggle-switch-label" htmlFor={"branchInfo"}>
                                        <span className="toggle-switch-inner" />
                                        <span className="toggle-switch-switch" />
                                    </label>
                                </div>
                                <span>No</span>
                            </div>
                                : <Input autoComplete={"off"} type={type} maxLength={maxLength ? maxLength : 200} name={name} id={name} defaultValue={value} value={getValue()} placeholder={title} onChange={(e) => handleInputChange(e.target.value)} />
                    }
                    {loading ? (<Progress animated color="info" value={100} />) : (name === "status" || columnData?.column?.columnType === "MIRROR" ? null : (
                        <>
                            <Button className="float-right submit-button my-2 py-1" type="button" size="sm" onClick={editTask} disabled={error || loading}>Submit</Button>
                            {canClearCurrentField() && (
                                <Button color="link" className="float-right my-2 py-1 mr-2" type="button" size="sm" onClick={() => handleInputChange(null)}>
                                    Clear
                                </Button>
                            )}
                        </>
                    ))}
                </div>
            </PopoverBody>
        </Popover>
    )
}

export default withApolloClient(EditTaskPopOver)
